import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Activity,
  AlertTriangle,
  ArrowDownToLine,
  ArrowLeft,
  ArrowRightLeft,
  Calendar,
  ChevronRight,
  Clock,
  CreditCard,
  Edit,
  FileText,
  HandshakeIcon,
  History,
  Loader2,
  Mail,
  MessageSquare,
  Minus,
  Pencil,
  Phone,
  Plus,
  Save,
  Search,
  User,
  UserPlus,
  X,
  Zap,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { LogCallModal } from '@/components/collections/action-modals';
import { CaseDetailsSidebar } from '@/components/collections/case-details-sidebar';
import { CaseSendEmailModal } from '@/components/collections/case-send-email-modal';
import {
  type CompleteActionData,
  CompleteActionDialog,
} from '@/components/collections/complete-action-dialog';
import {
  CreatePromiseDrawer,
  type PromiseFormData,
} from '@/components/collections/create-promise-drawer';
import { SelectTransactionsModal } from '@/components/collections/select-transactions-modal';
import { UpdateNextActionDialog } from '@/components/collections/update-next-action-dialog';
import { HelpSupportButton } from '@/components/help-support';
import { AddContactDialog } from '@/components/shared/add-contact-dialog';
import {
  ColumnConfigDialog,
  type ColumnDefinition,
} from '@/components/shared/column-config-dialog';
import { TransactionDetailSheet } from '@/components/transactions/transaction-detail-sheet';
import { TransactionTable } from '@/components/transactions/transaction-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { UniqueTableNamesForColumnConfiguration } from '@/config/unique-table-names-for-column-configuration';
import { useCompany } from '@/contexts/CompanyContext';
import { useAddCaseNote } from '@/hooks/useAddCaseNote';
import { useCaseAuditLogsGraphQL } from '@/hooks/useCaseAuditLogsGraphQL';
import {
  useCaseContacts,
  useCaseNotes,
  useCollectionCaseDetail,
} from '@/hooks/useCaseDetailData';
import { type CaseUnifiedActivity } from '@/hooks/useCaseRecentActivities';
import { useCaseRecentActivitiesGraphQL } from '@/hooks/useCaseRecentActivitiesGraphQL';
import { useUpdateCaseTransactions } from '@/hooks/useCaseTransactionLinks';
import {
  buildCaseTransactionExportFilter,
  CASE_TRANSACTION_EXPORT_COLUMN_MAP,
  useCaseTransactionsPaginatedGraphQL,
} from '@/hooks/useCaseTransactionsGraphQL';
import {
  mapPriorityToId,
  mapRiskLevelToId,
  mapStatusToId,
  type UpdateCollectionCaseRequest,
  useAssignCollectionCase,
  useUpdateCollectionCase,
} from '@/hooks/useCollectionCasesData';
import { useExportAll } from '@/hooks/useExportAll';
import { useScreenColumnPreferences } from '@/hooks/useScreenColumnPreferences';
import { useCompanyUsersList } from '@/hooks/useUsersData';
import { getGlobalDataStore } from '@/lib/mock-data/global-data-store';
import { cn, formatDateDDMMYYYY } from '@/lib/utils';
import { formatCurrency } from '@/lib/utils/formatters';
import { ExportProvider } from '@/providers/ExportProvider';
import {
  type CaseAuditLog,
  type CaseNote,
  type CollectionCase,
} from '@/types/collections';
import type { ColumnInput } from '@/types/export';
import { Forms } from '@/types/export';
import type { ColumnVisibility } from '@/types/transaction';
import { type Transaction } from '@/types/transaction';
import { type ContactUI } from '@/types/ui/customer-detail';

const DEFAULT_CASE_TRANSACTION_COLUMNS: ColumnDefinition[] = [
  {
    id: 'referenceNumber',
    label: 'Reference Number',
    visible: true,
    order: 0,
    locked: true,
  },
  { id: 'transactionDate', label: 'Transaction Date', visible: true, order: 1 },
  { id: 'dueDate', label: 'Due Date', visible: true, order: 2 },
  { id: 'customerName', label: 'Customer Name', visible: false, order: 3 },
  { id: 'transactionType', label: 'Type', visible: true, order: 4 },
  { id: 'currency', label: 'Currency', visible: true, order: 5 },
  { id: 'amount', label: 'Gross', visible: true, order: 6 },
  { id: 'outstandingAmount', label: 'Outstanding', visible: true, order: 7 },
  { id: 'status', label: 'Status', visible: true, order: 8 },
  { id: 'description', label: 'Description', visible: false, order: 9 },
  { id: 'createdBy', label: 'Created By', visible: false, order: 10 },
  { id: 'modifiedBy', label: 'Modified By', visible: false, order: 11 },
  { id: 'accountCode', label: 'Account', visible: false, order: 12 },
  { id: 'netAmount', label: 'Net', visible: false, order: 13 },
  { id: 'vatAmount', label: 'Tax', visible: false, order: 14 },
  { id: 'onDispute', label: 'External Dispute', visible: false, order: 15 },
  { id: 'subLedger', label: 'Sub Ledger', visible: false, order: 16 },
  { id: 'orderNumber', label: 'Order Number', visible: false, order: 17 },
  { id: 'flags', label: 'Flags', visible: true, order: 18 },
  { id: 'actions', label: 'Actions', visible: true, order: 19 },
];

const getPriorityBadgeVariant = (priority: string) => {
  switch (priority) {
    case 'High':
      return 'destructive';
    case 'Medium':
      return 'default';
    case 'Low':
      return 'secondary';
    default:
      return 'default';
  }
};

const getStatusBadgeColor = (status: string) => {
  switch (status) {
    case 'Active':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    case 'On Hold':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
    case 'Promised':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    case 'Escalated':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    case 'Closed':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    default:
      return '';
  }
};

const getAuditLogIcon = (action: CaseAuditLog['action']) => {
  switch (action) {
    case 'status_change':
      return ArrowRightLeft;
    case 'transaction_added':
      return Plus;
    case 'transaction_removed':
      return Minus;
    case 'priority_change':
      return AlertTriangle;
    case 'assignment_change':
      return UserPlus;
    case 'field_update':
      return Edit;
    case 'note_added':
      return FileText;
    case 'case_created':
      return History;
    default:
      return Activity;
  }
};

const getAuditLogColor = (action: CaseAuditLog['action']) => {
  switch (action) {
    case 'status_change':
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400';
    case 'transaction_added':
      return 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400';
    case 'transaction_removed':
      return 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400';
    case 'priority_change':
      return 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400';
    case 'assignment_change':
      return 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';
    case 'field_update':
      return 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400';
    case 'note_added':
      return 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400';
    case 'case_created':
      return 'bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400';
    default:
      return 'bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400';
  }
};

const getTagColor = (tag: CaseUnifiedActivity['tag']) => {
  switch (tag) {
    case 'Activity':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800';
    case 'Note':
      return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border-purple-200 dark:border-purple-800';
    case 'Communication':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800';
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400 border-gray-200 dark:border-gray-800';
  }
};

const getIconBackgroundColor = (tag: CaseUnifiedActivity['tag']) => {
  switch (tag) {
    case 'Activity':
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400';
    case 'Note':
      return 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';
    case 'Communication':
      return 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400';
    default:
      return 'bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400';
  }
};

const getUnifiedActivityIcon = (type: CaseUnifiedActivity['type']) => {
  switch (type) {
    case 'call':
      return Phone;
    case 'email':
      return Mail;
    case 'sms':
      return MessageSquare;
    case 'case_note':
    case 'note':
      return FileText;
    case 'payment':
      return CreditCard;
    default:
      return Activity;
  }
};

interface ActivityTimelineCardProps {
  activities: CaseUnifiedActivity[];
  isLoading: boolean;
  isEditMode: boolean;
  onAddNote?: (content: string) => Promise<void>;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
}

function matchesTransactionSearch(tx: Transaction, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  const ref = (tx.referenceNumber ?? '').toLowerCase();
  const desc = (tx.description ?? '').toLowerCase();
  const amount = String(tx.amount ?? '');
  const outstanding = String(tx.outstandingAmount ?? '');
  const txDate = tx.transactionDate
    ? formatDateDDMMYYYY(tx.transactionDate).toLowerCase()
    : '';
  const dueDate = tx.dueDate
    ? formatDateDDMMYYYY(tx.dueDate).toLowerCase()
    : '';
  const customerName = (tx.customerName ?? '').toLowerCase();
  return (
    ref.includes(q) ||
    desc.includes(q) ||
    amount.includes(q) ||
    outstanding.includes(q) ||
    txDate.includes(q) ||
    dueDate.includes(q) ||
    customerName.includes(q)
  );
}

function ActivityTimelineCard({
  activities,
  isLoading,
  isEditMode,
  onAddNote,
  hasMore,
  isFetchingMore,
  onLoadMore,
}: ActivityTimelineCardProps) {
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [isSavingNote, setIsSavingNote] = useState(false);

  const filteredActivities = useMemo(() => {
    if (selectedFilters.length === 0) return activities;
    return activities.filter((x) => selectedFilters.includes(x.tag));
  }, [activities, selectedFilters]);

  const activeFilterCount = selectedFilters.length;

  const clearFilters = () => {
    setSelectedFilters([]);
  };

  const handleAddNote = async () => {
    if (!newNoteContent.trim()) {
      toast.error('Please enter a note before saving');
      return;
    }
    if (!onAddNote) {
      toast.error('Note functionality not available');
      return;
    }
    setIsSavingNote(true);
    try {
      await onAddNote(newNoteContent.trim());
      setNewNoteContent('');
      toast.success('Note added successfully');
    } catch (error) {
      toast.error('Failed to add note. Please try again.');
      console.error('Error adding note:', error);
    } finally {
      setIsSavingNote(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <CardTitle>Activity Timeline</CardTitle>
            <CardDescription>
              {isEditMode
                ? 'Add new notes and view complete history of all activities, notes, and communications'
                : 'Complete history of all activities, notes, and communications'}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {filteredActivities.length}{' '}
              {filteredActivities.length === 1 ? 'item' : 'items'}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filter Controls */}
        <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Filter by Type
            </Label>
            {activeFilterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="h-7 text-xs"
              >
                <X className="h-3 w-3 mr-1" />
                Clear Filters ({activeFilterCount})
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant={
                selectedFilters.includes('Activity') ? 'default' : 'outline'
              }
              size="sm"
              onClick={() => {
                if (selectedFilters.includes('Activity')) {
                  setSelectedFilters(
                    selectedFilters.filter((x) => x !== 'Activity'),
                  );
                } else {
                  setSelectedFilters([...selectedFilters, 'Activity']);
                }
              }}
              className={cn(
                'h-8',
                selectedFilters.includes('Activity') &&
                  'bg-blue-600 hover:bg-blue-700 text-white dark:bg-blue-600 dark:hover:bg-blue-700',
              )}
            >
              <Activity className="h-4 w-4 mr-2" />
              Activity
            </Button>
            <Button
              variant={selectedFilters.includes('Note') ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                if (selectedFilters.includes('Note')) {
                  setSelectedFilters(
                    selectedFilters.filter((x) => x !== 'Note'),
                  );
                } else {
                  setSelectedFilters([...selectedFilters, 'Note']);
                }
              }}
              className={cn(
                'h-8',
                selectedFilters.includes('Note') &&
                  'bg-purple-600 hover:bg-purple-700 text-white dark:bg-purple-600 dark:hover:bg-purple-700',
              )}
            >
              <FileText className="h-4 w-4 mr-2" />
              Notes
            </Button>
            <Button
              variant={
                selectedFilters.includes('Communication')
                  ? 'default'
                  : 'outline'
              }
              size="sm"
              onClick={() => {
                if (selectedFilters.includes('Communication')) {
                  setSelectedFilters(
                    selectedFilters.filter((x) => x !== 'Communication'),
                  );
                } else {
                  setSelectedFilters([...selectedFilters, 'Communication']);
                }
              }}
              className={cn(
                'h-8',
                selectedFilters.includes('Communication') &&
                  'bg-green-600 hover:bg-green-700 text-white dark:bg-green-600 dark:hover:bg-green-700',
              )}
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Communications
            </Button>
          </div>
        </div>

        {/* Add Note Section - Only visible in Edit Mode */}
        {isEditMode && (
          <>
            <Separator />
            <div className="space-y-3 p-4 border rounded-lg bg-purple-50/50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-900">
              <Label
                htmlFor="newNote"
                className="text-sm font-medium flex items-center gap-2"
              >
                <FileText className="h-4 w-4" />
                Add New Note
              </Label>
              <Textarea
                id="newNote"
                value={newNoteContent}
                onChange={(e) => setNewNoteContent(e.target.value)}
                placeholder="Enter your note here..."
                rows={4}
                className="resize-none"
              />
              <div className="flex justify-end">
                <Button
                  onClick={handleAddNote}
                  disabled={isSavingNote || !newNoteContent.trim()}
                  size="sm"
                >
                  {isSavingNote ? (
                    <>
                      <Clock className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Add Note
                    </>
                  )}
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Timeline Display */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Clock className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredActivities.length > 0 ? (
          <>
            <Separator />
            <div className="space-y-4">
              {filteredActivities.map((activity, index) => {
                const Icon = getUnifiedActivityIcon(activity.type);
                return (
                  <div key={activity.id} className="relative">
                    {index !== filteredActivities.length - 1 && (
                      <div className="absolute left-4 top-10 h-full w-0.5 bg-border" />
                    )}
                    <div className="flex gap-4">
                      <div
                        className={cn(
                          'flex h-8 w-8 items-center justify-center rounded-full flex-shrink-0',
                          getIconBackgroundColor(activity.tag),
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge
                                variant="outline"
                                className={cn(
                                  'text-xs font-medium',
                                  getTagColor(activity.tag),
                                )}
                              >
                                {activity.tag}
                              </Badge>
                            </div>
                            <p className="text-sm text-foreground leading-relaxed">
                              {activity.description}
                            </p>
                            {activity.outcome && (
                              <p className="text-sm text-muted-foreground italic">
                                Outcome: {activity.outcome}
                              </p>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground whitespace-nowrap">
                            {format(activity.createdAt, 'MMM d, yyyy h:mm a')}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            <User className="h-3 w-3 mr-1" />
                            {activity.createdBy}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {hasMore && onLoadMore && (
              <div className="flex justify-center pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onLoadMore}
                  disabled={isFetchingMore}
                  className="text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300"
                >
                  {isFetchingMore ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Load more'
                  )}
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="text-sm font-medium">
              No activities match your filters
            </p>
            <p className="text-xs mt-1">Try adjusting your filter selection</p>
            {activeFilterCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearFilters}
                className="mt-4"
              >
                Clear All Filters
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function CaseDetailsPage() {
  return (
    <ExportProvider>
      <CaseDetailsPageInner />
    </ExportProvider>
  );
}

function CaseDetailsPageInner() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const { selectedCompany } = useCompany();

  const [caseDataOverride, setCaseDataOverride] =
    useState<CollectionCase | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedData, setEditedData] = useState<CollectionCase | null>(null);
  const [selectedTransactions, setSelectedTransactions] = useState<Set<number>>(
    new Set(),
  );
  const [notesHistory, setNotesHistory] = useState<CaseNote[]>([]);
  const [transactionDetailSheetOpen, setTransactionDetailSheetOpen] =
    useState(false);
  const [selectedTransactionDetail, setSelectedTransactionDetail] =
    useState<Transaction | null>(null);
  const [contacts, setContacts] = useState<ContactUI[]>([]);
  const [selectedContactId, setSelectedContactId] = useState<string>('');
  const [isCompleteActionDialogOpen, setIsCompleteActionDialogOpen] =
    useState(false);
  const [addTransactionModalOpen, setAddTransactionModalOpen] = useState(false);
  const [pendingTransactions, setPendingTransactions] = useState<Transaction[]>(
    [],
  );
  const [transactionSearchQuery, setTransactionSearchQuery] = useState('');
  const [txSortBy, setTxSortBy] = useState<string>('transactionDate');
  const [txSortOrder, setTxSortOrder] = useState<'asc' | 'desc'>('desc');
  const {
    columns: transactionColumns,
    savePreferences: saveColumnPreferences,
    resetToDefaults: resetColumnPreferences,
  } = useScreenColumnPreferences(
    UniqueTableNamesForColumnConfiguration.CaseDetailsTransactions,
    DEFAULT_CASE_TRANSACTION_COLUMNS,
  );
  const columnVisibility = useMemo(() => {
    const vis = {} as ColumnVisibility;
    transactionColumns.forEach((col) => {
      vis[col.id as keyof ColumnVisibility] = col.visible;
    });
    return vis;
  }, [transactionColumns]);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [logCallOpen, setLogCallOpen] = useState(false);
  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const [createPromiseOpen, setCreatePromiseOpen] = useState(false);
  const [showUpdateNextAction, setShowUpdateNextAction] = useState(false);
  const [addContactDialogOpen, setAddContactDialogOpen] = useState(false);
  const pendingNewContactId = useRef<string | null>(null);

  // Fetch active users for the Assigned To dropdown (company-scoped)
  const { data: activeUsers = [] } = useCompanyUsersList();

  const updateCaseMutation = useUpdateCollectionCase();
  const assignCaseMutation = useAssignCollectionCase();
  const updateCaseTransactionsMutation = useUpdateCaseTransactions();
  const addNoteMutation = useAddCaseNote();
  const queryClient = useQueryClient();

  // Fetch case data directly by ID
  const {
    data: directCaseData = null,
    isLoading: isDirectCaseLoading,
    isError: isDirectCaseError,
  } = useCollectionCaseDetail(caseId);

  const resolvedCaseData = directCaseData;

  // Use override for optimistic updates, otherwise use resolved data
  const caseData = caseDataOverride ?? resolvedCaseData;

  // Clear override when resolved data updates (server data arrives)
  useEffect(() => {
    if (resolvedCaseData && caseDataOverride) {
      setCaseDataOverride(null);
    }
  }, [resolvedCaseData, caseDataOverride]);

  // Reset edit mode when navigating to a different case
  useEffect(() => {
    setIsEditMode(false);
    setEditedData(null);
    setSelectedTransactions(new Set());
    pendingNewContactId.current = null;
  }, [caseId]);

  const { data: notesData = [] } = useCaseNotes(caseData?.id);
  const { data: contactsData = [] } = useCaseContacts(caseData?.customerId);

  // GraphQL paginated hooks
  const [txCurrentPage, setTxCurrentPage] = useState(1);
  const [txItemsPerPage, setTxItemsPerPage] = useState(100);

  const {
    activities: recentActivities,
    isLoading: activitiesLoading,
    isFetchingMore: activitiesFetchingMore,
    hasMore: activitiesHasMore,
    loadMore: activitiesLoadMore,
    refetch: refetchActivities,
  } = useCaseRecentActivitiesGraphQL(caseData?.id);

  const {
    auditLogs: auditLogsData,
    isLoading: auditLogsLoading,
    isFetchingMore: auditLogsFetchingMore,
    hasMore: auditLogsHasMore,
    loadMore: auditLogsLoadMore,
    refetch: refetchAuditLogs,
  } = useCaseAuditLogsGraphQL(
    caseData?.id,
    caseData?.displayOutstandingAmount ?? caseData?.baseAmount ?? 0,
    caseData?.customerEmail || '',
  );

  // Case transactions via dedicated GraphQL endpoint
  const { transactions: caseTransactions, pagination: txPagination } =
    useCaseTransactionsPaginatedGraphQL(caseData?.id?.toString(), {
      page: txCurrentPage,
      pageSize: txItemsPerPage,
      search: transactionSearchQuery,
      sortBy: txSortBy,
      sortOrder: txSortOrder,
    });

  const filteredPendingTransactions = useMemo(() => {
    if (!isEditMode) return [];
    const serverIds = new Set(
      (caseTransactions as unknown as Transaction[]).map((tx) => tx.id),
    );
    const dedupedPending = pendingTransactions.filter(
      (tx) => !serverIds.has(tx.id),
    );
    return transactionSearchQuery
      ? dedupedPending.filter((tx) =>
          matchesTransactionSearch(tx, transactionSearchQuery),
        )
      : dedupedPending;
  }, [
    caseTransactions,
    pendingTransactions,
    isEditMode,
    transactionSearchQuery,
  ]);

  const visibleTransactions = useMemo(() => {
    const serverTxs = caseTransactions as unknown as Transaction[];
    if (!isEditMode) return serverTxs;

    const serverTotal = txPagination.totalItems;
    const pageStart = (txCurrentPage - 1) * txItemsPerPage;
    const pageEnd = txCurrentPage * txItemsPerPage;
    const pendingStart = Math.max(0, pageStart - serverTotal);
    const pendingEnd = Math.max(0, pageEnd - serverTotal);
    const pendingSlice = filteredPendingTransactions.slice(
      pendingStart,
      pendingEnd,
    );

    return [...serverTxs, ...pendingSlice];
  }, [
    caseTransactions,
    filteredPendingTransactions,
    isEditMode,
    txPagination.totalItems,
    txCurrentPage,
    txItemsPerPage,
  ]);

  const displayOutstandingTotal = useMemo(() => {
    if (isEditMode) {
      const serverTotal = caseTransactions
        .filter((x) => selectedTransactions.has(x.id))
        .reduce(
          (sum, x) =>
            sum +
            (x.displayOutstandingAmount ?? x.outstandingAmount ?? x.amount),
          0,
        );
      const pendingTotal = pendingTransactions.reduce(
        (sum, x) =>
          sum + (x.displayOutstandingAmount ?? x.outstandingAmount ?? x.amount),
        0,
      );
      return serverTotal + pendingTotal;
    }
    return caseTransactions.reduce(
      (sum, x) =>
        sum + (x.displayOutstandingAmount ?? x.outstandingAmount ?? x.amount),
      0,
    );
  }, [isEditMode, selectedTransactions, caseTransactions, pendingTransactions]);

  // Sync hook data to local state for notes and contacts
  useEffect(() => {
    if (notesData.length > 0) {
      setNotesHistory(notesData);
    }
  }, [notesData]);

  useEffect(() => {
    if (contactsData.length > 0) {
      setContacts(contactsData);

      if (pendingNewContactId.current) {
        const newContact = contactsData.find(
          (c) => c.id === pendingNewContactId.current,
        );
        if (newContact) {
          setSelectedContactId(newContact.id);
          setEditedData((prev) =>
            prev
              ? {
                  ...prev,
                  customerEmail: newContact.email,
                  customerPhone: newContact.phone,
                }
              : prev,
          );
          pendingNewContactId.current = null;
          return;
        }
      }

      setSelectedContactId((prev) => {
        if (caseData?.contactId) {
          return caseData.contactId;
        }
        if (!prev || prev === 'none') {
          const primaryContact = contactsData.find((c) => c.isPrimary);
          const contactToSelect = primaryContact || contactsData[0];
          return contactToSelect ? contactToSelect.id : prev;
        }
        return prev;
      });
    }
  }, [contactsData, caseData?.contactId]);

  // Server-side sort change handler
  const handleSortChange = useCallback(
    (newSortBy: string, newSortOrder: 'asc' | 'desc') => {
      setTxSortBy(newSortBy);
      setTxSortOrder(newSortOrder);
    },
    [],
  );

  // Server-side XLSX export — build columns and filter
  const getExportFilter = useCallback(():
    | Record<string, unknown>
    | undefined => {
    return buildCaseTransactionExportFilter(caseData?.id?.toString());
  }, [caseData?.id]);

  const getExportSummary = useCallback((): string | undefined => {
    return transactionSearchQuery || undefined;
  }, [transactionSearchQuery]);

  const getVisibleExportColumns = useCallback((): ColumnInput[] => {
    return transactionColumns
      .filter(
        (col) => col.visible && col.id in CASE_TRANSACTION_EXPORT_COLUMN_MAP,
      )
      .map((col) => ({
        name: CASE_TRANSACTION_EXPORT_COLUMN_MAP[col.id],
        displayName: col.label,
      }));
  }, [transactionColumns]);

  const getAllExportColumns = useCallback((): ColumnInput[] => {
    return DEFAULT_CASE_TRANSACTION_COLUMNS.filter(
      (col) => col.id in CASE_TRANSACTION_EXPORT_COLUMN_MAP,
    ).map((col) => ({
      name: CASE_TRANSACTION_EXPORT_COLUMN_MAP[col.id],
      displayName: col.label,
    }));
  }, []);

  const { triggerExportAll: triggerExportCurrentView } = useExportAll({
    form: Forms.CaseTransactions,
    getColumns: getVisibleExportColumns,
    getFilter: getExportFilter,
    getSummary: getExportSummary,
    fileName: `case-transactions-${caseData?.customerName || 'case'}-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  const { triggerExportAll: triggerExportAllColumns } = useExportAll({
    form: Forms.CaseTransactions,
    getColumns: getAllExportColumns,
    getFilter: getExportFilter,
    getSummary: getExportSummary,
    fileName: `case-transactions-all-columns-${caseData?.customerName || 'case'}-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  const handleExportCurrentView = useCallback(() => {
    triggerExportCurrentView();
    toast.info('Export started. You will be notified when the file is ready.');
  }, [triggerExportCurrentView]);

  const handleExportAllColumns = useCallback(() => {
    triggerExportAllColumns();
    toast.info('Export started. You will be notified when the file is ready.');
  }, [triggerExportAllColumns]);

  const handleAddTransactionsToCase = useCallback(
    (transactions: Transaction[]) => {
      setPendingTransactions((prev) => {
        const existingIds = new Set(prev.map((t) => t.id));
        const newTxs = transactions.filter((tx) => !existingIds.has(tx.id));
        return [...prev, ...newTxs];
      });
      setSelectedTransactions((prev) => {
        const newSet = new Set(prev);
        for (const tx of transactions) {
          newSet.add(tx.id);
        }
        return newSet;
      });
      setAddTransactionModalOpen(false);
    },
    [],
  );

  if (!caseData) {
    if (isDirectCaseLoading) {
      return (
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <p className="text-muted-foreground">Loading case details...</p>
          </div>
        </div>
      );
    }

    if (isDirectCaseError) {
      return (
        <div className="flex items-center justify-center h-screen">
          <div className="text-center space-y-4">
            <AlertTriangle className="h-12 w-12 mx-auto text-destructive" />
            <p className="text-muted-foreground">
              Failed to load case details.
            </p>
            <Button
              variant="outline"
              onClick={() => navigate('/collections/queue')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Queue
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center space-y-4">
          <AlertTriangle className="h-12 w-12 mx-auto text-muted-foreground" />
          <p className="text-muted-foreground">Case not found.</p>
          <Button
            variant="outline"
            onClick={() => navigate('/collections/queue')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Queue
          </Button>
        </div>
      </div>
    );
  }

  const handleBack = () => {
    navigate('/collections/queue');
  };

  const handleEditClick = () => {
    const data = { ...caseData };

    // If a contact is selected, populate email/phone from the contact
    if (selectedContactId && selectedContactId !== 'none') {
      const selectedContact = contacts.find((c) => c.id === selectedContactId);
      if (selectedContact) {
        data.customerEmail = selectedContact.email;
        data.customerPhone = selectedContact.phone;
      }
    }

    setEditedData(data);
    setIsEditMode(true);
    // Select all case transactions by default in edit mode
    setSelectedTransactions(new Set(caseTransactions.map((x) => x.id)));
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedData(null);
    setSelectedTransactions(new Set());
    setPendingTransactions([]);
    pendingNewContactId.current = null;
    // Restore selectedContactId to the saved value from caseData
    if (caseData?.contactId) {
      setSelectedContactId(caseData.contactId);
    } else {
      const primaryContact = contacts.find((c) => c.isPrimary);
      const contactToSelect = primaryContact || contacts[0];
      setSelectedContactId(contactToSelect ? contactToSelect.id : '');
    }
  };

  const handleContactChange = (contactId: string) => {
    setSelectedContactId(contactId);

    if (!editedData) return;

    if (contactId === 'none') {
      // Clear email and phone when "None" is selected
      setEditedData({
        ...editedData,
        customerEmail: '',
        customerPhone: '',
      });
    } else {
      // Find the selected contact and auto-populate
      const selectedContact = contacts.find((c) => c.id === contactId);
      if (selectedContact) {
        setEditedData({
          ...editedData,
          customerEmail: selectedContact.email,
          customerPhone: selectedContact.phone,
        });
      }
    }
  };

  const handleSaveEdit = () => {
    if (!editedData) return;

    // Email format validation (only if email is provided)
    if (editedData.customerEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(editedData.customerEmail)) {
        toast.error('Please enter a valid email address');
        return;
      }
    }

    // Validate at least one transaction is selected
    if (selectedTransactions.size === 0) {
      toast.error('At least one transaction must be selected');
      return;
    }

    // Validate all selected transactions share the same currency
    const allTxs = [...caseTransactions, ...pendingTransactions];
    const selectedTxs = allTxs.filter((x) => selectedTransactions.has(x.id));
    const currencies = [
      ...new Set(selectedTxs.map((tx) => tx.currency).filter(Boolean)),
    ];
    if (currencies.length > 1) {
      toast.error(
        `All transactions must share the same currency. Found: ${currencies.join(', ')}`,
      );
      return;
    }

    // Enforce case currency immutability: if case has a currency set, transactions must match it
    if (
      caseData?.currency &&
      currencies.length === 1 &&
      currencies[0] !== caseData.currency
    ) {
      toast.error(
        `Cannot change case currency. Case currency is ${caseData.currency} but selected transactions are in ${currencies[0]}.`,
      );
      return;
    }

    // Case amount uses DisplayOutstandingAmount (correct for both base and foreign currency)
    const serverAmount = caseTransactions
      .filter((x) => selectedTransactions.has(x.id))
      .reduce(
        (sum, x) =>
          sum + (x.displayOutstandingAmount ?? x.outstandingAmount ?? x.amount),
        0,
      );
    const pendingAmount = pendingTransactions.reduce(
      (sum, x) =>
        sum + (x.displayOutstandingAmount ?? x.outstandingAmount ?? x.amount),
      0,
    );
    const updatedAmount = serverAmount + pendingAmount;

    const request: UpdateCollectionCaseRequest = {
      caseId: Number(editedData.id),
      priorityId: mapPriorityToId(editedData.priority),
      statusId: mapStatusToId(editedData.status),
      strategyId: editedData.strategy?.id
        ? Number(editedData.strategy.id)
        : undefined,
      amount: updatedAmount,
      nextAction: editedData.nextAction,
      nextActionDate: editedData.nextActionDate
        ? editedData.nextActionDate instanceof Date
          ? editedData.nextActionDate.toISOString().split('T')[0]
          : String(editedData.nextActionDate)
        : undefined,
      riskLevelId: editedData.riskLevel
        ? mapRiskLevelToId(editedData.riskLevel)
        : undefined,
      contactId:
        selectedContactId && selectedContactId !== 'none'
          ? Number(selectedContactId)
          : undefined,
      notes: editedData.notes,
    };

    const assignedUserChanged =
      editedData.assignedUserId !== caseData?.assignedUserId;

    const finalizeSave = () => {
      const selectedTransactionsList = [
        ...new Set(Array.from(selectedTransactions).map(Number)),
      ];
      const savedData = { ...editedData, amount: updatedAmount };
      updateCaseTransactionsMutation.mutate(
        {
          caseId: String(editedData.id),
          transactionIds: selectedTransactionsList,
        },
        {
          onSuccess: () => {
            const message =
              selectedTransactionsList.length > 0
                ? `Case updated successfully with ${selectedTransactionsList.length} transaction(s) linked`
                : 'Case updated successfully';
            toast.success(message);
            setCaseDataOverride(savedData);
            setIsEditMode(false);
            setEditedData(null);
            setSelectedTransactions(new Set());
            setPendingTransactions([]);
            queryClient.invalidateQueries({
              queryKey: ['collection-case-detail', caseData?.id],
            });
            queryClient.invalidateQueries({
              queryKey: ['collection-cases-list'],
            });
            queryClient.invalidateQueries({
              queryKey: ['case-notes', caseData?.id],
            });
            queryClient.invalidateQueries({
              queryKey: ['case-transactions-graphql-list'],
            });
            queryClient.invalidateQueries({
              queryKey: ['case-transactions-graphql-count'],
            });
            refetchActivities();
            refetchAuditLogs();
          },
          onError: (error) => {
            toast.error(
              `Case updated but failed to save transaction links: ${error.message}`,
            );
            setCaseDataOverride(savedData);
            setIsEditMode(false);
            setEditedData(null);
            setSelectedTransactions(new Set());
            setPendingTransactions([]);
            queryClient.invalidateQueries({
              queryKey: ['collection-case-detail', caseData?.id],
            });
            queryClient.invalidateQueries({
              queryKey: ['collection-cases-list'],
            });
            queryClient.invalidateQueries({
              queryKey: ['case-notes', caseData?.id],
            });
            queryClient.invalidateQueries({
              queryKey: ['case-transactions-graphql-list'],
            });
            queryClient.invalidateQueries({
              queryKey: ['case-transactions-graphql-count'],
            });
            refetchActivities();
            refetchAuditLogs();
          },
        },
      );
    };

    updateCaseMutation.mutate(request, {
      onSuccess: () => {
        if (assignedUserChanged && editedData.assignedUserId) {
          assignCaseMutation.mutate(
            {
              caseId: Number(editedData.id),
              assignedToUserId: editedData.assignedUserId,
            },
            {
              onSuccess: () => finalizeSave(),
              onError: (error) => {
                toast.error(
                  `Case updated but failed to assign user: ${error.message}`,
                );
                finalizeSave();
              },
            },
          );
        } else {
          finalizeSave();
        }
      },
      onError: (error) => {
        toast.error(`Failed to save case: ${error.message}`);
      },
    });
  };

  const handleCompleteAction = (data: CompleteActionData) => {
    if (!caseData || !caseId) return;

    // Update the case with the completion data
    const updates: Partial<CollectionCase> = {
      notes: data.notes,
    };

    // If a next action was selected, update it
    if (data.nextAction && data.nextActionDate) {
      updates.nextAction = data.nextAction;
      updates.nextActionDate = data.nextActionDate as Date;
    } else {
      // No next action selected - clear it
      updates.nextAction = 'No action scheduled';
      updates.nextActionDate = new Date();
    }

    // If status changed, update it
    if (data.newStatus) {
      updates.status = data.newStatus as
        | 'Active'
        | 'On Hold'
        | 'Closed'
        | 'Promised';
    }

    // Update the case in the global store
    getGlobalDataStore().updateCase(caseId, updates);

    // Add activity entry
    const activityEntry = {
      id: `activity-${Date.now()}`,
      type: 'action_completed' as const,
      description: `Completed action: ${caseData.nextAction}`,
      details: data.outcome,
      timestamp: new Date(),
      user: 'Current User',
    };
    getGlobalDataStore().addActivity(caseId, activityEntry);

    // Add note to history
    const noteEntry: CaseNote = {
      id: `note-${Date.now()}`,
      caseId: caseId,
      content: `Action Completed: ${caseData.nextAction}\n\nOutcome: ${data.outcome}\n\nNotes: ${data.notes}`,
      createdBy: 'Current User',
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    setNotesHistory([noteEntry, ...notesHistory]);

    // Refresh case data
    const updatedCase = { ...caseData, ...updates };
    setCaseDataOverride(updatedCase);
    queryClient.invalidateQueries({ queryKey: ['collection-cases-list'] });
    queryClient.invalidateQueries({
      queryKey: ['collection-case-detail', caseId],
    });
    refetchActivities();
    refetchAuditLogs();
  };

  const handleRecordPromise = (promiseData: PromiseFormData) => {
    if (!caseData || !caseId) return;

    // Add activity entry
    const activityEntry = {
      id: `activity-${Date.now()}`,
      type: 'promise_created' as const,
      description: `Promise to Pay recorded for ${formatCurrency(promiseData.promisedAmount, caseData?.currency || selectedCompany?.currency || 'GBP')}`,
      details: `Promise Date: ${format(promiseData.promiseDate, 'MMM d, yyyy')}`,
      timestamp: new Date(),
      user: 'Current User',
    };
    getGlobalDataStore().addActivity(caseId, activityEntry);

    // Add note to history
    const noteEntry: CaseNote = {
      id: `note-${Date.now()}`,
      caseId: caseId,
      content: `Promise to Pay Recorded\n\nAmount: ${formatCurrency(promiseData.promisedAmount, caseData?.currency || selectedCompany?.currency || 'GBP')}\nPromise Date: ${format(promiseData.promiseDate, 'MMM d, yyyy')}\nConfirmation Method: ${promiseData.confirmationMethod}\n\nNotes: ${promiseData.notes}`,
      createdBy: 'Current User',
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    setNotesHistory([noteEntry, ...notesHistory]);

    // Refresh case data from server - backend determines if status should change to "Promised"
    queryClient.invalidateQueries({ queryKey: ['collection-cases-list'] });
    queryClient.invalidateQueries({
      queryKey: ['collection-case-detail', caseId],
    });
    refetchActivities();
    refetchAuditLogs();

    // Close the drawer
    setCreatePromiseOpen(false);

    // Show success message
    toast.success('Promise to Pay recorded successfully');
  };

  const currentData = isEditMode && editedData ? editedData : caseData;

  const handleColumnConfigApply = (columns: ColumnDefinition[]) => {
    saveColumnPreferences(columns);
  };

  const handleAddNote = async (content: string) => {
    if (!caseData?.id) return;

    await addNoteMutation.mutateAsync({ caseId: caseData.id, notes: content });
    refetchActivities();
    refetchAuditLogs();
  };

  const handleViewTransactionDetail = (transaction: Transaction) => {
    setSelectedTransactionDetail(transaction);
    setTransactionDetailSheetOpen(true);
  };

  const renderCustomerInfoCard = () => (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          Customer Information
        </CardTitle>
        <CardDescription className="text-xs">
          Customer details and contact information
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 pb-16">
        {/* Two-column grid layout - 2 rows */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 w-full">
          {/* Row 1: Customer and Contact Name */}
          {/* Customer Name & ID - Left column */}
          <div className="space-y-0.5">
            <p className="text-xs font-medium text-muted-foreground">
              Customer
            </p>
            <p className="text-base font-semibold">
              <span className="text-muted-foreground font-normal">
                [{currentData.customerAccountNumber}]
              </span>{' '}
              {currentData.customerName}
            </p>
          </div>

          {/* Contact Name - Right column */}
          <div className="space-y-0.5">
            <Label
              htmlFor="contact"
              className="text-xs font-medium text-muted-foreground flex items-center gap-1"
            >
              <User className="h-3 w-3" />
              Contact Name
              {isEditMode && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 ml-1 p-0"
                  onClick={() => setAddContactDialogOpen(true)}
                  title="Add new contact"
                >
                  <Plus className="h-3 w-3" />
                </Button>
              )}
            </Label>
            {isEditMode && editedData ? (
              <Select
                value={selectedContactId}
                onValueChange={handleContactChange}
              >
                <SelectTrigger id="contact" className="h-9">
                  <SelectValue placeholder="Select a contact to auto-populate email and phone" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {contacts.map((contact) => (
                    <SelectItem key={contact.id} value={contact.id}>
                      {`${contact.firstName} ${contact.surname}`}
                      {contact.isPrimary && ' (Primary)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm">
                {(() => {
                  if (selectedContactId && selectedContactId !== 'none') {
                    const c = contacts.find((c) => c.id === selectedContactId);
                    if (c) return `${c.firstName} ${c.surname}`;
                  }
                  // Fallback: show primary contact or first contact from list
                  const primary = contacts.find((c) => c.isPrimary);
                  const fallback = primary || contacts[0];
                  return fallback
                    ? `${fallback.firstName} ${fallback.surname}`
                    : 'No contacts available';
                })()}
              </p>
            )}
          </div>

          {/* Row 2: Email Address and Phone Number */}
          {/* Email Address - Left column */}
          <div className="space-y-0.5">
            <Label
              htmlFor="email"
              className="text-xs font-medium text-muted-foreground flex items-center gap-1"
            >
              <Mail className="h-3 w-3" />
              Email Address
            </Label>
            {isEditMode && editedData ? (
              <Input
                id="email"
                type="email"
                value={editedData.customerEmail}
                readOnly
                disabled
                className="h-9 bg-muted"
                placeholder="Select a contact to view email"
              />
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => setSendEmailOpen(true)}
                  className="gap-2 bg-teal-600 hover:bg-teal-700 text-white h-7 px-2 shrink-0"
                >
                  <Mail className="h-3.5 w-3.5" />
                </Button>
                <p className="text-sm break-all">{currentData.customerEmail}</p>
              </div>
            )}
          </div>

          {/* Phone Number - Right column */}
          <div className="space-y-0.5">
            <Label
              htmlFor="phone"
              className="text-xs font-medium text-muted-foreground flex items-center gap-1"
            >
              <Phone className="h-3 w-3" />
              Phone Number
            </Label>
            {isEditMode && editedData ? (
              <Input
                id="phone"
                type="tel"
                value={editedData.customerPhone}
                readOnly
                disabled
                className="h-9 bg-muted"
                placeholder="Select a contact to view phone"
              />
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => setLogCallOpen(true)}
                  className="gap-2 bg-teal-600 hover:bg-teal-700 text-white h-7 px-2 shrink-0"
                >
                  <Phone className="h-3.5 w-3.5" />
                </Button>
                <p className="text-sm">{currentData.customerPhone}</p>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // A manual case has no strategyId (strategy.id is empty)
  const isManualCase = !currentData.strategy.id;

  const renderNextActionCard = () => (
    <Card className="h-[180px]">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          Next Action
        </CardTitle>
        <CardDescription>Upcoming action details</CardDescription>
        {isEditMode && editedData && isManualCase ? (
          <div className="space-y-2 mt-4">
            <Select
              value={editedData.nextAction}
              onValueChange={(value) =>
                setEditedData({ ...editedData, nextAction: value })
              }
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Follow-up call">Follow-up call</SelectItem>
                <SelectItem value="Send email">Send email</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
              <Input
                type="date"
                value={
                  editedData.nextActionDate instanceof Date
                    ? format(editedData.nextActionDate, 'yyyy-MM-dd')
                    : editedData.nextActionDate
                }
                onChange={(e) =>
                  setEditedData({
                    ...editedData,
                    nextActionDate: new Date(e.target.value),
                  })
                }
                className="h-8 w-auto"
              />
            </div>
          </div>
        ) : (
          <div className="mt-4">
            <div className="p-3 rounded-lg bg-muted/30">
              <div className="flex items-center justify-between gap-4">
                <p className="text-sm font-semibold">
                  {currentData.nextAction}
                </p>
                <div className="flex items-center gap-2 shrink-0">
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">
                    Due:{' '}
                    {currentData.nextAction === 'No further actions scheduled'
                      ? 'n/a'
                      : format(
                          currentData.nextActionDate instanceof Date
                            ? currentData.nextActionDate
                            : new Date(currentData.nextActionDate),
                          'MMM d, yyyy',
                        )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardHeader>
    </Card>
  );

  const renderCaseManagementCard = () => (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Activity className="h-4 w-4 text-green-600 dark:text-green-400" />
          Case Management
        </CardTitle>
        <CardDescription className="text-xs">
          Case details and collection information
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 pb-2">
        {/* Top Row: Case ID, Outstanding Amount, Days in Collection */}
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-0.5">
            <p className="text-xs font-medium text-muted-foreground">Case ID</p>
            <p className="text-sm font-semibold">{currentData.caseNumber || currentData.caseId}</p>
          </div>
          <div className="space-y-0.5">
            <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <CreditCard className="h-3 w-3" />
              Total Outstanding
            </p>
            <p className="text-base font-bold text-red-600 dark:text-red-400">
              {formatCurrency(
                displayOutstandingTotal,
                caseData?.currency || selectedCompany?.currency || 'GBP',
              )}
            </p>
          </div>
          <div className="space-y-0.5">
            <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Days in Collection
            </p>
            <p className="text-sm font-semibold">
              {currentData.daysInCollection} days
            </p>
          </div>
        </div>
        {/* Second Row: Status, Priority, Collection Strategy */}
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-0.5">
            <Label
              htmlFor="status"
              className="text-xs font-medium text-muted-foreground"
            >
              Case Status
            </Label>
            {isEditMode && editedData ? (
              <Select
                value={editedData.status}
                onValueChange={(value: 'Active' | 'On Hold' | 'Closed') =>
                  setEditedData({ ...editedData, status: value })
                }
              >
                <SelectTrigger id="status" className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Promised">Promised</SelectItem>
                  <SelectItem value="Escalated">Escalated</SelectItem>
                  <SelectItem value="On Hold">On Hold</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <Badge
                className={cn(getStatusBadgeColor(currentData.status), 'w-fit')}
              >
                {currentData.status}
              </Badge>
            )}
          </div>

          <div className="space-y-0.5">
            <Label
              htmlFor="priority"
              className="text-xs font-medium text-muted-foreground"
            >
              Priority Level
            </Label>
            {isEditMode && editedData ? (
              <Select
                value={editedData.priority}
                onValueChange={(value: 'High' | 'Medium' | 'Low') =>
                  setEditedData({ ...editedData, priority: value })
                }
              >
                <SelectTrigger id="priority" className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <Badge
                variant={getPriorityBadgeVariant(currentData.priority)}
                className="w-fit"
              >
                {currentData.priority}
              </Badge>
            )}
          </div>

          <div className="space-y-0.5">
            <p className="text-xs font-medium text-muted-foreground">
              Collection Strategy
            </p>
            <p className="text-sm font-semibold">{currentData.strategy.name}</p>
          </div>
        </div>
        {/* Third Row: Assigned User & Collection Stage */}
        <div className="grid grid-cols-3 gap-3">
          {/* Assigned User - first column */}
          <div className="space-y-0.5">
            <Label
              htmlFor="assignedTo"
              className="text-xs font-medium text-muted-foreground flex items-center gap-1"
            >
              <UserPlus className="h-3 w-3" />
              Assigned User
            </Label>
            {isEditMode && editedData ? (
              <Select
                value={
                  editedData.assignedUserId
                    ? String(editedData.assignedUserId)
                    : 'unassigned'
                }
                onValueChange={(value) => {
                  if (value === 'unassigned') {
                    setEditedData({
                      ...editedData,
                      assignedTo: undefined,
                      assignedUserId: undefined,
                    });
                  } else {
                    const selectedUser = activeUsers.find(
                      (x) => x.id === value,
                    );
                    if (selectedUser) {
                      setEditedData({
                        ...editedData,
                        assignedTo: `${selectedUser.firstName} ${selectedUser.lastName}`,
                        assignedUserId: Number(selectedUser.id),
                      });
                    }
                  }
                }}
              >
                <SelectTrigger id="assignedTo" className="h-9">
                  <SelectValue placeholder="Select user..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {activeUsers.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm">
                {currentData.assignedTo || 'Unassigned'}
              </p>
            )}
          </div>
          {/* Collection Stage - second column, aligned under Priority Level */}
          <div className="space-y-0.5">
            <Label className="text-xs font-medium text-muted-foreground">
              Collection Stage
            </Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all"
                        style={{
                          width: `${(currentData.strategyPhase.currentPhase / currentData.strategyPhase.totalPhases) * 100}%`,
                        }}
                      />
                    </div>
                    <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
                      {currentData.strategyPhase.currentPhase}/
                      {currentData.strategyPhase.totalPhases}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="font-medium">
                    {currentData.strategyPhase.phaseName}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Phase {currentData.strategyPhase.currentPhase} of{' '}
                    {currentData.strategyPhase.totalPhases}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div></div> {/* Empty third column */}
        </div>
      </CardContent>
    </Card>
  );

  const renderCaseNotesCard = () => (
    <Card className="h-[180px]">
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              Case Notes
            </CardTitle>
            <CardDescription>Latest notes and updates</CardDescription>
          </div>
          <div className="flex-1">
            {isEditMode && editedData ? (
              <Textarea
                value={editedData.notes || ''}
                onChange={(e) =>
                  setEditedData({ ...editedData, notes: e.target.value })
                }
                placeholder="Add case notes here..."
                className="h-[80px] resize-none overflow-y-auto"
              />
            ) : (
              <div className="h-[80px] overflow-hidden">
                {currentData.notes ? (
                  <div className="p-3 rounded-lg bg-muted/30 h-full overflow-hidden">
                    <p className="text-sm whitespace-pre-wrap line-clamp-3">
                      {currentData.notes}
                    </p>
                  </div>
                ) : (
                  <div className="p-3 rounded-lg bg-muted/30 h-full">
                    <p className="text-sm text-muted-foreground italic">
                      No notes added yet
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </CardHeader>
    </Card>
  );

  const renderTransactionsCard = () => (
    <Card className="min-w-0">
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5 text-green-600 dark:text-green-400" />
            <CardTitle className="text-lg">Transactions</CardTitle>
            <Badge variant="secondary" className="ml-2">
              {txPagination.totalItems + filteredPendingTransactions.length}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            {isEditMode && (
              <Button
                variant="default"
                size="sm"
                onClick={() => setAddTransactionModalOpen(true)}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Transaction
              </Button>
            )}
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Quick search..."
                className="pl-8 h-9 w-[200px]"
                value={transactionSearchQuery}
                onChange={(e) => setTransactionSearchQuery(e.target.value)}
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  title="Export to Excel"
                >
                  <ArrowDownToLine className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleExportCurrentView}>
                  Export Current View
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportAllColumns}>
                  Export All Columns
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <ColumnConfigDialog
              columns={transactionColumns}
              onApply={handleColumnConfigApply}
              onReset={resetColumnPreferences}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {isEditMode && pendingTransactions.length > 0 && (
          <div className="mb-3 flex items-center gap-2 text-sm text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-3 py-2 rounded-lg border border-green-200 dark:border-green-800">
            <Plus className="h-4 w-4" />
            <span className="font-medium">
              {pendingTransactions.length} pending transaction
              {pendingTransactions.length === 1 ? '' : 's'}
            </span>
            <span className="text-muted-foreground">
              — will be saved when you click Save Changes
            </span>
          </div>
        )}
        <TransactionTable
          transactions={visibleTransactions as unknown as Transaction[]}
          columnVisibility={columnVisibility}
          orderedColumns={transactionColumns}
          onViewTransaction={handleViewTransactionDetail}
          showCustomerColumn={false}
          displayAmounts={true}
          enableSelection={isEditMode}
          selectedRows={selectedTransactions}
          onSelectRow={(id, checked) => {
            setSelectedTransactions((prev) => {
              const newSet = new Set(prev);
              if (checked) {
                newSet.add(id);
              } else {
                newSet.delete(id);
              }
              return newSet;
            });
          }}
          onSelectAll={(checked) => {
            if (checked) {
              setSelectedTransactions(
                new Set(visibleTransactions.map((tx) => tx.id)),
              );
            } else {
              setSelectedTransactions(new Set());
            }
          }}
          currentPage={txCurrentPage}
          pageSize={txItemsPerPage}
          totalItems={
            txPagination.totalItems + filteredPendingTransactions.length
          }
          onPageChange={setTxCurrentPage}
          onPageSizeChange={(size) => {
            setTxItemsPerPage(size);
            setTxCurrentPage(1);
          }}
          onSortChange={handleSortChange}
          renderActions={(transaction) => (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleViewTransactionDetail(transaction);
              }}
            >
              View
            </Button>
          )}
        />

        <Separator className="my-4" />
        <div className="flex justify-between items-center">
          <span className="font-semibold">Total Outstanding:</span>
          <span className="text-xl font-bold text-red-600 dark:text-red-400">
            {formatCurrency(
              displayOutstandingTotal,
              caseData?.currency || selectedCompany?.currency || 'GBP',
            )}
          </span>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left Sidebar */}
      <div
        className={cn(
          'relative transition-all duration-300 ease-in-out shrink-0',
          isSidebarCollapsed ? 'w-0' : 'w-80',
        )}
      >
        <CaseDetailsSidebar
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden relative">
        {/* Vertical Tab/Handle - Only visible when sidebar is collapsed */}
        {isSidebarCollapsed && (
          <button
            onClick={() => setIsSidebarCollapsed(false)}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-40 group"
            title="Show Cases"
          >
            <div className="flex items-center bg-teal-500 hover:bg-teal-600 text-white shadow-lg rounded-r-lg transition-all duration-200 group-hover:pr-2">
              <div className="flex flex-col items-center justify-center py-4 px-2 gap-1">
                <ChevronRight className="h-5 w-5" />
                <div className="writing-mode-vertical text-xs font-medium tracking-wider">
                  CASES
                </div>
              </div>
            </div>
          </button>
        )}

        <div className="flex flex-col h-full overflow-hidden">
          {/* Header */}
          <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="px-6 py-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h1 className="text-3xl font-bold tracking-tight">
                      Case Details
                    </h1>
                    <p className="text-muted-foreground">
                      Complete information for case {caseData.caseNumber || caseData.caseId}
                    </p>
                  </div>
                </div>
                {!isEditMode ? (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="default"
                      onClick={handleBack}
                      className="gap-2"
                    >
                      Back to Queue
                    </Button>
                    <Button
                      variant="default"
                      size="default"
                      onClick={() => setCreatePromiseOpen(true)}
                      className="gap-2"
                    >
                      <HandshakeIcon className="h-4 w-4" />
                      Record Promise
                    </Button>
                    <Button
                      variant="outline"
                      size="default"
                      onClick={handleEditClick}
                      className="gap-2"
                    >
                      <Pencil className="h-4 w-4" />
                      Edit Case
                    </Button>
                    <HelpSupportButton screenId="case-details" />
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="default"
                      onClick={handleBack}
                      className="gap-2"
                    >
                      Back to Queue
                    </Button>
                    <Button
                      variant="outline"
                      size="default"
                      onClick={handleCancelEdit}
                      className="gap-2"
                    >
                      <X className="h-4 w-4" />
                      Cancel
                    </Button>
                    <Button
                      size="default"
                      onClick={handleSaveEdit}
                      className="gap-2"
                    >
                      <Save className="h-4 w-4" />
                      Save Changes
                    </Button>
                    <HelpSupportButton screenId="case-details" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tabs Content */}
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="flex-1 flex flex-col overflow-hidden"
          >
            <div className="px-6 pt-4 border-b shrink-0">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger
                  value="activity"
                  className="flex items-center gap-2"
                >
                  Activity
                  <Badge
                    variant="secondary"
                    className="ml-1 h-5 min-w-5 px-1.5"
                  >
                    {recentActivities.length}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="audit">Audit Log</TabsTrigger>
              </TabsList>
            </div>

            <div className="flex-1 overflow-y-auto">
              <TabsContent
                value="overview"
                className="px-6 py-4 space-y-4 mt-0"
              >
                {/* Two-Panel Layout: Customer Information & Case Management */}
                <div className="grid gap-6 lg:grid-cols-[550px_1fr]">
                  {/* Row 1 Left: Customer Information */}
                  {renderCustomerInfoCard()}

                  {/* Row 1 Right: Case Management */}
                  {renderCaseManagementCard()}

                  {/* Row 2 Left: Next Action */}
                  {renderNextActionCard()}

                  {/* Row 2 Right: Case Notes */}
                  {renderCaseNotesCard()}
                </div>

                {/* Transactions Section */}
                {renderTransactionsCard()}
              </TabsContent>

              <TabsContent
                value="activity"
                className="px-6 py-4 space-y-4 mt-0"
              >
                <ActivityTimelineCard
                  activities={recentActivities}
                  isLoading={activitiesLoading}
                  isEditMode={isEditMode}
                  onAddNote={handleAddNote}
                  hasMore={activitiesHasMore}
                  isFetchingMore={activitiesFetchingMore}
                  onLoadMore={activitiesLoadMore}
                />
              </TabsContent>

              <TabsContent value="audit" className="px-6 py-4 space-y-4 mt-0">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <History className="h-5 w-5" />
                      Audit Log
                    </CardTitle>
                    <CardDescription>
                      Complete chronological history of all case modifications
                      and changes
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {auditLogsData.map((log, index) => {
                        const Icon = getAuditLogIcon(log.action);
                        const colorClass = getAuditLogColor(log.action);

                        return (
                          <div key={log.id} className="relative">
                            {index !== auditLogsData.length - 1 && (
                              <div className="absolute left-4 top-10 h-full w-0.5 bg-border" />
                            )}
                            <div className="flex gap-4">
                              <div
                                className={cn(
                                  'flex h-8 w-8 items-center justify-center rounded-full flex-shrink-0',
                                  colorClass,
                                )}
                              >
                                <Icon className="h-4 w-4" />
                              </div>
                              <div className="flex-1 space-y-2">
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1 space-y-1">
                                    <p className="font-medium text-sm">
                                      {log.description}
                                    </p>

                                    {/* Status/Priority/Field Changes */}
                                    {(log.action === 'status_change' ||
                                      log.action === 'priority_change' ||
                                      log.action === 'field_update' ||
                                      log.action === 'assignment_change') && (
                                      <div className="flex items-center gap-2 text-sm">
                                        <Badge
                                          variant="outline"
                                          className="text-xs"
                                        >
                                          {log.oldValue || 'None'}
                                        </Badge>
                                        <ArrowRightLeft className="h-3 w-3 text-muted-foreground" />
                                        <Badge
                                          variant="outline"
                                          className="text-xs"
                                        >
                                          {log.newValue}
                                        </Badge>
                                      </div>
                                    )}

                                    {/* Transaction Added */}
                                    {log.action === 'transaction_added' &&
                                      (log.transactionNumber || log.transactionId) && (
                                        <div className="text-sm text-muted-foreground space-y-1">
                                          <div className="flex items-center gap-2">
                                            <span className="font-medium">
                                              {log.transactionNumber || log.transactionId}
                                            </span>
                                            <span>•</span>
                                            <span className="font-medium text-green-600 dark:text-green-400">
                                              {formatCurrency(
                                                log.transactionAmount ?? 0,
                                                caseData?.currency ||
                                                  selectedCompany?.currency ||
                                                  'GBP',
                                              )}
                                            </span>
                                          </div>
                                          {log.transactionDate && (
                                            <div className="text-xs">
                                              Invoice Date:{' '}
                                              {format(
                                                log.transactionDate,
                                                'MMM d, yyyy',
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      )}

                                    {/* Transaction Removed */}
                                    {log.action === 'transaction_removed' &&
                                      (log.transactionNumber || log.transactionId) && (
                                        <div className="text-sm text-muted-foreground space-y-1">
                                          <div className="flex items-center gap-2">
                                            <span className="font-medium">
                                              {log.transactionNumber || log.transactionId}
                                            </span>
                                            <span>•</span>
                                            <span className="font-medium text-red-600 dark:text-red-400">
                                              {formatCurrency(
                                                log.transactionAmount ?? 0,
                                                caseData?.currency ||
                                                  selectedCompany?.currency ||
                                                  'GBP',
                                              )}
                                            </span>
                                          </div>
                                          {log.transactionDate && (
                                            <div className="text-xs">
                                              Invoice Date:{' '}
                                              {format(
                                                log.transactionDate,
                                                'MMM d, yyyy',
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                  </div>
                                  <p className="text-xs text-muted-foreground whitespace-nowrap">
                                    {format(
                                      log.createdAt,
                                      'MMM d, yyyy h:mm a',
                                    )}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-xs">
                                    <User className="h-3 w-3 mr-1" />
                                    {log.createdBy}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {auditLogsLoading && auditLogsData.length === 0 && (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      </div>
                    )}

                    {!auditLogsLoading && auditLogsData.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p className="text-sm">
                          No audit log entries found for this case.
                        </p>
                      </div>
                    )}

                    {auditLogsHasMore && (
                      <div className="flex justify-center pt-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={auditLogsLoadMore}
                          disabled={auditLogsFetchingMore}
                          className="text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300"
                        >
                          {auditLogsFetchingMore ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Loading...
                            </>
                          ) : (
                            'Load more'
                          )}
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </div>
          </Tabs>

          {/* Transaction Detail Sheet (rendered outside scrollable area as overlays) */}
          <TransactionDetailSheet
            open={transactionDetailSheetOpen}
            onOpenChange={setTransactionDetailSheetOpen}
            transaction={selectedTransactionDetail}
          />

          <CompleteActionDialog
            open={isCompleteActionDialogOpen}
            onOpenChange={setIsCompleteActionDialogOpen}
            currentAction={
              caseData
                ? {
                    action: caseData.nextAction,
                    dueDate:
                      typeof caseData.nextActionDate === 'string'
                        ? caseData.nextActionDate
                        : format(caseData.nextActionDate, 'yyyy-MM-dd'),
                  }
                : null
            }
            caseId={caseId || ''}
            onComplete={handleCompleteAction}
          />

          {/* Log Call Modal */}
          <LogCallModal
            open={logCallOpen}
            onOpenChange={setLogCallOpen}
            customerName={caseData.customerName}
            customerPhone={caseData.customerPhone}
            caseId={caseData.id}
            onSuccess={() => {
              refetchActivities();
              refetchAuditLogs();
              if (isManualCase) {
                setShowUpdateNextAction(true);
              }
            }}
          />

          {/* Send Email Modal */}
          <CaseSendEmailModal
            open={sendEmailOpen}
            onOpenChange={setSendEmailOpen}
            caseId={caseData.id}
            customerId={caseData.customerId}
            customerName={caseData.customerName}
            accountNumber={caseData.customerAccountNumber || ''}
            onSuccess={() => {
              refetchActivities();
              refetchAuditLogs();
              if (isManualCase) {
                setShowUpdateNextAction(true);
              }
            }}
          />

          {/* Update Next Action Dialog (for manual cases) */}
          <UpdateNextActionDialog
            open={showUpdateNextAction}
            onOpenChange={setShowUpdateNextAction}
            caseData={caseData}
            onUpdated={() => {
              queryClient.invalidateQueries({
                queryKey: ['collection-case-detail', caseId],
              });
              queryClient.invalidateQueries({
                queryKey: ['collection-cases-list'],
              });
              refetchActivities();
              refetchAuditLogs();
            }}
          />

          {/* Add Transaction Modal */}
          {caseData && (
            <SelectTransactionsModal
              open={addTransactionModalOpen}
              onOpenChange={setAddTransactionModalOpen}
              customerId={Number(caseData.customerId)}
              context="case"
              caseId={Number(caseData.id)}
              onAddTransactions={handleAddTransactionsToCase}
              currency={caseData.currency ?? undefined}
            />
          )}

          {/* Create Promise Drawer */}
          <CreatePromiseDrawer
            open={createPromiseOpen}
            onOpenChange={setCreatePromiseOpen}
            prePopulatedCustomer={{
              customerId: caseData.customerId,
              customerName: caseData.customerName,
              accountNumber: caseData.customerAccountNumber || '',
            }}
            prePopulatedTransactions={caseTransactions.map((transaction) => {
              // Calculate days overdue from dueDate
              const daysOverdue = transaction.dueDate
                ? Math.max(
                    0,
                    Math.floor(
                      (new Date().getTime() -
                        new Date(transaction.dueDate).getTime()) /
                        (1000 * 60 * 60 * 24),
                    ),
                  )
                : 0;

              return {
                id: transaction.id.toString(),
                headerReference: transaction.referenceNumber,
                invoiceDate: new Date(transaction.transactionDate),
                originalAmount: transaction.amount,
                balanceRemaining:
                  transaction.outstandingAmount || transaction.amount,
                daysOverdue: daysOverdue,
                status: transaction.status ?? null,
              };
            })}
            prePopulatedCaseId={caseData.id}
            prePopulatedCaseNumber={caseData.caseNumber || caseData.caseId}
            triggerSource="case-details-button"
            onSave={handleRecordPromise}
          />

          <AddContactDialog
            customerId={caseData?.customerId ?? ''}
            defaultCompany={
              currentData?.customerName ||
              currentData?.customerAccountNumber ||
              ''
            }
            open={addContactDialogOpen}
            onOpenChange={setAddContactDialogOpen}
            onContactCreated={(newId) => {
              pendingNewContactId.current = newId;
              setSelectedContactId(newId);
            }}
          />
        </div>
      </div>
    </div>
  );
}
